import React, { FC, Fragment, useRef, useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Typography, Box, Grid, useMediaQuery, Theme, Fab } from '@mui/material';
import PageTitle from 'components/shared/page-title/page-title.component';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { useQuery } from '@apollo/client';
import { OrderEventFilters } from 'models/order-event';
import Loader from 'components/shared/loader/loader.component';
import PatientHistoryFilters from 'components/patient-history/patient-history-filters';
import { getPatientOrdersTemplate } from 'apollo/templates/patient';
import { PatientOrders } from 'models/patient';
import { useTranslation } from 'react-i18next';
import OrderEventsTimeline from 'components/shared/order-events-timeline/order-events-timeline.component';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

const PatientHistory: FC = () => {
    const { t: translate } = useTranslation(['patient']);
    const isLargeScreen = useMediaQuery((theme: Theme) => theme.breakpoints.up('lg'));
    const bottomRef = useRef<HTMLDivElement>(null);
    const { pin } = useParams<{ pin: string }>();
    const [patientOrders, setPatientOrders] = useState<PatientOrders>();
    // DD-3772: an external user sees only the orders they are linked to, so a
    // patient can have none for them — the server then answers null.
    const [hasNoOrders, setHasNoOrders] = useState(false);
    const [showOrderIds, setShowOrderIds] = useState<string[]>([]);
    const [orderEventFilters, setOrderEventFilters] = useState<OrderEventFilters>({
        chat: true,
        files: true,
        modified: true,
        comments: true,
        status: true,
    });
    const { showBoundary } = useDenthubErrorBoundary();

    useQuery(getPatientOrdersTemplate, {
        variables: {
            personalIdentityNumber: pin,
        },
        onCompleted: ({ patientOrders }) => {
            setPatientOrders(patientOrders ?? undefined);
            setHasNoOrders(!patientOrders);
        },
        onError(error) {
            showBoundary(error);
        },
    });

    useEffect(() => {
        if (patientOrders) {
            setShowOrderIds(patientOrders.orderIds.map((patientOrderId) => patientOrderId.id));
        }
    }, [patientOrders]);

    const handleFilterChange = (newFilters: OrderEventFilters) => {
        setOrderEventFilters(newFilters);
    };

    const handleOrdersChange = (newIds: string[]) => {
        setShowOrderIds(newIds);
    };

    const onScrollBottomClick = () => {
        window.scrollTo({
            top: document.documentElement.scrollHeight,
            behavior: 'smooth',
        });
    };

    const renderTimeline = () => {
        if (patientOrders) {
            return (
                <OrderEventsTimeline
                    orderIds={patientOrders.orderIds.map((patientOrderId) => patientOrderId.id)}
                    visibleOrderIds={showOrderIds}
                    filters={orderEventFilters}
                    isMultipleOrders={true}
                />
            );
        }
        if (hasNoOrders) {
            return (
                <Typography variant="body2" data-test="patient-history.no-orders">
                    {translate('NO_PATIENT_ORDERS')}
                </Typography>
            );
        }
        return <Loader />;
    };

    return (
        <Fragment>
            <Grid container spacing={2}>
                <Grid item sm={12} lg={3}>
                    <Box sx={{ position: 'sticky', top: 80 }}>
                        {patientOrders ? (
                            <Fragment>
                                <Box className="mouseflow-mask">
                                    <PageTitle
                                        headingStyle="h2"
                                        title={`${patientOrders.patient.firstName} ${patientOrders.patient.lastName}`}
                                    />
                                    <Typography variant="subtitle1">
                                        {patientOrders.patient.personalIdentityNumber} -{' '}
                                        {translate(patientOrders.patient.category)}
                                    </Typography>
                                    {isLargeScreen ? (
                                        <Box mt={2}>
                                            <PatientHistoryFilters
                                                orderIds={patientOrders.orderIds}
                                                onFilterChange={handleFilterChange}
                                                onOrdersChange={handleOrdersChange}
                                            />
                                        </Box>
                                    ) : undefined}
                                </Box>
                            </Fragment>
                        ) : undefined}
                    </Box>
                </Grid>
                <Grid item md={12} lg={9}>
                    {renderTimeline()}
                    <div id="bottom" ref={bottomRef} />
                </Grid>
            </Grid>
            <Fab
                sx={{
                    position: 'fixed',
                    bottom: (theme) => theme.spacing(2),
                    right: (theme) => theme.spacing(2),
                }}
                onClick={onScrollBottomClick}
            >
                <KeyboardArrowDownIcon />
            </Fab>
        </Fragment>
    );
};

export default PatientHistory;
